import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { getJobStatusPosition, isJobStatus, ShipmentJobStatus } from '../../models/delivery/shipment-job.status';
import { ShipmentDeliveryOperationStatus, ShipmentOperationTimelineEvent } from '../../models/workflow/shipment-workflow.model';
import { fleetResourceAgencyLabel, fleetResourceOwnerLabel, fleetResourceOwnershipTypeLabel, fleetResourceSourceCompanyLabel } from '../../../shared/utils/fleet-resource-ownership.util';

export type ShipmentDispatchPhaseKey = 'ASSIGNED' | 'ACCEPTED' | 'LOADING' | 'IN_TRANSIT' | 'UNLOADING' | 'DELIVERED';
export type ShipmentDispatchStepState = 'done' | 'active' | 'pending' | 'cancelled';

export interface ShipmentDispatchProgressStep {
    key: ShipmentDispatchPhaseKey;
    label: string;
    icon: string;
    state: ShipmentDispatchStepState;
    timestamp?: string;
}

export interface ShipmentDispatchProgress {
    label: string;
    percent: number;
    severity: string;
    summary: string;
    cancelled: boolean;
    steps: ShipmentDispatchProgressStep[];
}

interface ShipmentDispatchPhase {
    key: ShipmentDispatchPhaseKey;
    label: string;
    icon: string;
    statuses: ShipmentJobStatus[];
}

const PHASES: ShipmentDispatchPhase[] = [
    { key: 'ASSIGNED', label: 'Assigned', icon: 'fa-solid fa-clipboard-list', statuses: [ShipmentJobStatus.JOB_DETAILS_ENTRY, ShipmentJobStatus.SEARCHING_VEHICLE, ShipmentJobStatus.SELECTING_VEHICLE, ShipmentJobStatus.JOB_INVOKE_DRIVER, ShipmentJobStatus.JOB_DRIVER_NEGOTIATING, ShipmentJobStatus.JOB_ADVERTISE_INVOKE] },
    { key: 'ACCEPTED', label: 'Accepted', icon: 'fa-solid fa-user-check', statuses: [ShipmentJobStatus.JOB_DRIVER_ACCEPTED, ShipmentJobStatus.DRIVER_ORDER_PROCESSING] },
    { key: 'LOADING', label: 'Loading', icon: 'fa-solid fa-truck-ramp-box', statuses: [ShipmentJobStatus.DRIVER_REACHED_PICKUP, ShipmentJobStatus.DRIVER_DOCUMENT_VERIFICATION, ShipmentJobStatus.JOB_PAYLOAD_LOADING, ShipmentJobStatus.PAYLOAD_QUANTITY_VERIFICATION, ShipmentJobStatus.PAYLOAD_CONDITION_CHECK, ShipmentJobStatus.DELIVERY_DOCUMENTS_UPLOAD, ShipmentJobStatus.PAYLOAD_SEALING_PROCESS, ShipmentJobStatus.DRIVER_ORDER_SIGNOFF] },
    { key: 'IN_TRANSIT', label: 'In Transit', icon: 'fa-solid fa-truck-fast', statuses: [ShipmentJobStatus.DRIVER_STARTED_TRIP, ShipmentJobStatus.DRIVER_EN_ROUTE, ShipmentJobStatus.DRIVER_DELAY_ON_TRIP] },
    { key: 'UNLOADING', label: 'Unloading', icon: 'fa-solid fa-box-open', statuses: [ShipmentJobStatus.DRIVER_REACHED_DROPOFF, ShipmentJobStatus.RECIPIENT_VERIFICATION_PROCESS, ShipmentJobStatus.JOB_PAYLOAD_UNLOADING, ShipmentJobStatus.PAYLOAD_SEALING_CHECK, ShipmentJobStatus.PAYLOAD_QUANTITY_CHECK, ShipmentJobStatus.PAYLOAD_CONDITION_RECHECK] },
    { key: 'DELIVERED', label: 'Delivered', icon: 'fa-solid fa-circle-check', statuses: [ShipmentJobStatus.JOB_COMPLETION_ACKNOWLEDGE, ShipmentJobStatus.DRIVER_JOB_COMPLETED] }
];

const PHASE_INDEX = PHASES.reduce((result, phase, index) => ({ ...result, [phase.key]: index }), {} as Record<ShipmentDispatchPhaseKey, number>);

export function formatShipmentDispatchStatus(status: string | undefined): string {
    if (!status) return 'Pending';
    return status.replace(/^JOB_/, '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}

/** Pure presentation logic shared by the shipment overview and Dispatch panel. */
export class ShipmentDispatchPresenter {
    constructor(private readonly formatTimestamp: (timestamp: number | undefined) => string) {}

    deliveryProgress(job: ShipmentDelivery, statuses: Record<number, ShipmentDeliveryOperationStatus>): ShipmentDispatchProgress {
        const operationStatus = this.operationStatusFor(job, statuses);
        const cancelled = this.isCancelled(job, operationStatus);
        const activeKey = cancelled ? 'ASSIGNED' : this.resolvePhase(job, operationStatus);
        const activeIndex = PHASE_INDEX[activeKey];
        const percent = cancelled ? 0 : Math.round(((activeIndex + 1) / PHASES.length) * 100);
        const steps = PHASES.map((phase, index) => ({
            key: phase.key,
            label: phase.label,
            icon: phase.icon,
            state: (cancelled ? 'cancelled' : index < activeIndex ? 'done' : index === activeIndex ? activeKey === 'DELIVERED' ? 'done' : 'active' : 'pending') as ShipmentDispatchStepState,
            timestamp: this.phaseTimestamp(job, phase, operationStatus)
        }));
        const activePhase = PHASES[activeIndex];
        const label = cancelled ? 'Cancelled' : activePhase.label;
        return { label, percent, severity: this.progressSeverity(label), summary: cancelled ? 'Delivery cancelled' : (operationStatus?.currentPhase || `${percent}% complete`), cancelled, steps };
    }

    progressStepMeta(step: ShipmentDispatchProgressStep): string {
        if (step.timestamp) return step.timestamp;
        if (step.state === 'done') return 'Completed';
        if (step.state === 'active') return 'Current phase';
        return step.state === 'cancelled' ? 'Cancelled' : 'Pending';
    }

    progressBarStyleClass(progress: ShipmentDispatchProgress): string {
        return progress.cancelled ? 'delivery-progress-bar is-danger' : 'delivery-progress-bar';
    }

    deliverySeverity(status: string | undefined): string {
        switch (status) {
            case 'JOB_DRIVER_ACCEPTED': case 'JOB_COMPLETED': case 'JOB_COMPLETION_ACKNOWLEDGE': return 'success';
            case 'JOB_INVOKE_DRIVER': case 'JOB_DRIVER_ASSIGNED': return 'warn';
            case 'JOB_CANCELLED': return 'danger';
            default: return 'info';
        }
    }

    driverName(job: ShipmentDelivery): string { return job.deliveryDriver?.legalName || job.deliveryDriver?.name || 'Driver not assigned'; }

    driverMeta(job: ShipmentDelivery): string {
        const driver = job.deliveryDriver;
        if (!driver) return 'Assign a driver to start dispatch coordination.';
        return [fleetResourceOwnerLabel(driver), fleetResourceAgencyLabel(driver), fleetResourceSourceCompanyLabel(driver), driver.phoneNumber, driver.licenseNumber ? `License ${driver.licenseNumber}` : undefined].filter(Boolean).join(' | ') || 'Driver profile available';
    }

    vehicleLabel(job: ShipmentDelivery): string { return job.deliveryVehicle?.plateNumber || job.deliveryVehicle?.registrationNumber || 'Vehicle not assigned'; }

    vehicleMeta(job: ShipmentDelivery): string {
        const vehicle = job.deliveryVehicle;
        if (!vehicle) return 'Select a vehicle compatible with this shipment.';
        return [fleetResourceOwnerLabel(vehicle), fleetResourceAgencyLabel(vehicle), fleetResourceSourceCompanyLabel(vehicle), vehicle.type?.name, vehicle.config?.name, vehicle.capacity?.name, vehicle.model?.name, vehicle.year?.year].filter(Boolean).join(' | ') || vehicle.registrationNumber || 'Vehicle profile available';
    }

    assignmentSource(job: ShipmentDelivery): string {
        const driverType = job.deliveryDriver ? fleetResourceOwnershipTypeLabel(job.deliveryDriver) : null;
        const vehicleType = job.deliveryVehicle ? fleetResourceOwnershipTypeLabel(job.deliveryVehicle) : null;
        return driverType && vehicleType && driverType !== vehicleType ? `Driver ${driverType} / Vehicle ${vehicleType}` : driverType || vehicleType || 'Unassigned';
    }

    commercialLine(job: ShipmentDelivery, canViewPricing: boolean, formatAmount: (value: number) => string): string {
        if (!canViewPricing) return job.paymentTerms || 'Commercials restricted';
        return [job.fareQuote != null ? `Quote ${formatAmount(job.fareQuote)}` : undefined, job.jobWorth != null ? `Worth ${formatAmount(job.jobWorth)}` : undefined, job.paymentTerms || undefined].filter(Boolean).join(' | ') || 'Commercials pending';
    }

    readiness(job: ShipmentDelivery): string {
        if (job.customerConfirmed && job.driverConfirmed) return 'Customer and driver confirmed';
        if (job.customerConfirmed) return 'Customer confirmed';
        if (job.driverConfirmed) return 'Driver confirmed';
        return job.driverJobAccessed ? 'Driver accessed job' : 'Awaiting dispatch action';
    }

    updatedAt(job: ShipmentDelivery, statuses: Record<number, ShipmentDeliveryOperationStatus>): string {
        const operationStatus = this.operationStatusFor(job, statuses);
        const timestamps = [job.workflowUpdatedAt, ...Object.values(job.jobStatusMap ?? {}), ...(operationStatus?.timelineEvents ?? []).map(event => this.eventTime(event)).filter((value): value is number => typeof value === 'number' && value > 0)].filter((value): value is number => typeof value === 'number' && value > 0);
        return timestamps.length ? this.formatTimestamp(Math.max(...timestamps)) : 'No activity yet';
    }

    milestones(job: ShipmentDelivery): { label: string; date: string }[] {
        return Object.entries(job.jobStatusMap ?? {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([status, timestamp]) => ({ label: formatShipmentDispatchStatus(status), date: this.formatTimestamp(timestamp) }));
    }

    linkedReleaseCount(job: ShipmentDelivery, statuses: Record<number, ShipmentDeliveryOperationStatus>): number {
        return this.operationStatusFor(job, statuses)?.orderReleases?.length ?? 0;
    }

    private resolvePhase(job: ShipmentDelivery, operationStatus?: ShipmentDeliveryOperationStatus): ShipmentDispatchPhaseKey {
        const operationPhase = this.phaseFromOperationStatus(operationStatus);
        if (operationPhase) return operationPhase;
        const workflow = String(operationStatus?.deliveryStatus || job.workflowState || '').toUpperCase();
        if (['DELIVERY_COMPLETE', 'CUSTOMER_ACKNOWLEDGED', 'COMPLETED'].includes(workflow)) return 'DELIVERED';
        const status = this.highestJobStatus(job);
        if (status) return (PHASES.find(phase => phase.statuses.includes(status))?.key ?? 'ASSIGNED');
        if (String(job.jobStatus || '').toUpperCase() === 'JOB_COMPLETED') return 'DELIVERED';
        return workflow === 'ACCEPTED' ? 'ACCEPTED' : workflow === 'IN_PROGRESS' ? 'IN_TRANSIT' : 'ASSIGNED';
    }

    private phaseFromOperationStatus(operationStatus?: ShipmentDeliveryOperationStatus): ShipmentDispatchPhaseKey | undefined {
        if (!operationStatus) return undefined;
        const deliveryStatus = String(operationStatus.deliveryStatus || '').toUpperCase();
        if (['DELIVERY_COMPLETE', 'CUSTOMER_ACKNOWLEDGED', 'COMPLETED'].includes(deliveryStatus)) return 'DELIVERED';
        const phase = String(operationStatus.currentPhase || '').toUpperCase();
        const active = phase.includes('WORKING') || phase.includes('ARRIVED') || phase.includes('ACTIVE') || phase.includes('AT ');
        if (phase.includes('UNLOADING') || (phase.includes('DROPOFF') && active)) return 'UNLOADING';
        if (phase.includes('LOADING') || (phase.includes('PICKUP') && active)) return 'LOADING';
        if (this.isComplete(operationStatus.dropoffProgress)) return 'DELIVERED';
        if (this.hasStopState(operationStatus, 'DROPOFF', ['ARRIVED', 'WORKING', 'EVIDENCE_PENDING', 'COMPLETE'])) return 'UNLOADING';
        if (this.isComplete(operationStatus.pickupProgress)) return 'IN_TRANSIT';
        if (this.hasStopState(operationStatus, 'PICKUP', ['ARRIVED', 'WORKING', 'EVIDENCE_PENDING', 'COMPLETE'])) return 'LOADING';
        return deliveryStatus === 'ACCEPTED' ? 'ACCEPTED' : deliveryStatus === 'IN_PROGRESS' ? 'IN_TRANSIT' : undefined;
    }

    private highestJobStatus(job: ShipmentDelivery): ShipmentJobStatus | undefined {
        const statuses = [job.jobStatus, ...Object.keys(job.jobStatusMap ?? {})].filter((status): status is ShipmentJobStatus => isJobStatus(String(status)));
        return statuses.reduce((highest, status) => !highest || getJobStatusPosition(status) > getJobStatusPosition(highest) ? status : highest, undefined as ShipmentJobStatus | undefined);
    }

    private phaseTimestamp(job: ShipmentDelivery, phase: ShipmentDispatchPhase, operationStatus?: ShipmentDeliveryOperationStatus): string | undefined {
        const operationTime = this.operationPhaseTime(operationStatus, phase.key);
        if (operationTime) return this.formatTimestamp(operationTime);
        const values = phase.statuses.map(status => job.jobStatusMap?.[status]).filter((value): value is number => typeof value === 'number' && value > 0);
        return values.length ? this.formatTimestamp(Math.min(...values)) : undefined;
    }

    private isCancelled(job: ShipmentDelivery, operationStatus?: ShipmentDeliveryOperationStatus): boolean {
        const workflow = String(operationStatus?.deliveryStatus || job.workflowState || '').toUpperCase();
        const status = String(job.jobStatus || '').toUpperCase();
        const statuses = Object.keys(job.jobStatusMap ?? {}).map(value => value.toUpperCase());
        return workflow === 'CANCELLED' || ['JOB_DRIVER_CANCELLED', 'JOB_DRIVER_REJECTED', 'JOB_CANCELLED'].includes(status) || statuses.some(value => ['JOB_DRIVER_CANCELLED', 'JOB_DRIVER_REJECTED', 'JOB_CANCELLED'].includes(value));
    }

    private operationStatusFor(job: ShipmentDelivery, statuses: Record<number, ShipmentDeliveryOperationStatus>): ShipmentDeliveryOperationStatus | undefined {
        return job.deliveryId ? statuses[job.deliveryId] : undefined;
    }

    private isComplete(progress?: { total?: number; completed?: number }): boolean { return (progress?.total ?? 0) > 0 && (progress?.completed ?? 0) >= (progress?.total ?? 0); }

    private hasStopState(status: ShipmentDeliveryOperationStatus, type: string, states: string[]): boolean {
        const stateSet = new Set(states.map(value => value.toUpperCase()));
        return [status.activeStop, status.nextStop, ...(status.stops ?? [])].filter(Boolean).some(stop => String(stop?.stopType || '').toUpperCase() === type && stateSet.has(String(stop?.workflowState || '').toUpperCase()));
    }

    private operationPhaseTime(status: ShipmentDeliveryOperationStatus | undefined, phase: ShipmentDispatchPhaseKey): number | undefined {
        const values = (status?.timelineEvents ?? []).filter(event => this.eventMatchesPhase(event, phase)).map(event => this.eventTime(event)).filter((value): value is number => typeof value === 'number' && value > 0);
        return values.length ? Math.max(...values) : undefined;
    }

    private eventMatchesPhase(event: ShipmentOperationTimelineEvent, phase: ShipmentDispatchPhaseKey): boolean {
        const text = `${event.level || ''} ${event.nextState || ''} ${event.title || ''} ${event.description || ''}`.toUpperCase();
        if (phase === 'ACCEPTED') return text.includes('ACCEPTED');
        if (phase === 'LOADING') return text.includes('PICKUP') || text.includes('LOADING') || text.includes('LOADED') || text.includes('PICKED');
        if (phase === 'IN_TRANSIT') return text.includes('IN_PROGRESS') || text.includes('EN ROUTE') || text.includes('IN TRANSIT');
        if (phase === 'UNLOADING') return text.includes('DROPOFF') || text.includes('UNLOADING') || text.includes('DELIVERED');
        return phase === 'DELIVERED' && String(event.level || '').toUpperCase() === 'DELIVERY' && (text.includes('DELIVERY_COMPLETE') || text.includes('CUSTOMER_ACKNOWLEDGED') || text.includes('COMPLETED'));
    }

    private eventTime(event: ShipmentOperationTimelineEvent): number | undefined {
        if (!event?.eventAt) return undefined;
        if (typeof event.eventAt === 'number') return event.eventAt;
        const value = event.eventAt instanceof Date ? event.eventAt.getTime() : new Date(event.eventAt).getTime();
        return Number.isFinite(value) ? value : undefined;
    }

    private progressSeverity(label: string): string {
        return label === 'Delivered' ? 'success' : label === 'Cancelled' ? 'danger' : ['Loading', 'In Transit', 'Unloading'].includes(label) ? 'warn' : 'info';
    }
}
